import { useCallback, useEffect, useState } from "react";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, statusColors, statusLabels, getErrorMessage } from "@/features/shared";
import {
  AddSupplierCategoryFormDialog,
  EditSupplierCategoryFormDialog,
} from "@/features/supplier-categories";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const SupplierCategories = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [supplierCategoryId, setSupplierCategoryId] = useState(null);
  const [supplierCategories, setSupplierCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setRowCount, paginationParams, dataGridPaginationProps, resetParams } =
    useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadSupplierCategories = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("/supplier-categories", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
        },
      });
      setSupplierCategories(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat kategori pemasok"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadSupplierCategories();
  }, [loadSupplierCategories]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_SUPPLIER_CATEGORIES) && (
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
        >
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama kategori pemasok"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={supplierCategories}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 2,
              sortable: false,
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: (params) => {
                const actions = [];
                if (hasPermission(PermissionEnum.UPDATE_SUPPLIER_CATEGORIES)) {
                  actions.push(
                    <IconButton
                      key="edit"
                      onClick={() => setSupplierCategoryId(params.id)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>

      {/* Add */}
      {isAddDialogOpen && (
        <AddSupplierCategoryFormDialog
          onClose={() => setIsAddDialogOpen(false)}
          onSubmitted={loadSupplierCategories}
        />
      )}

      {/* Edit */}
      {supplierCategoryId != null && (
        <EditSupplierCategoryFormDialog
          supplierCategoryId={supplierCategoryId}
          onClose={() => setSupplierCategoryId(null)}
          onSubmitted={loadSupplierCategories}
        />
      )}
    </PageContainer>
  );
};
